import type { CodeSnippetLang, ConsoleOperation } from './types';

export const CODE_SNIPPET_LANGS: CodeSnippetLang[] = ['curl', 'python', 'r', 'javascript'];

export const CODE_SNIPPET_LABELS: Record<CodeSnippetLang, string> = {
  curl: 'cURL',
  python: 'Python',
  r: 'R',
  javascript: 'JavaScript',
};

const HTTR_VERBS = new Set(['get', 'post', 'put', 'patch', 'delete', 'head']);

function escapeRSingleQuoted(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export function buildCodeSnippets(options: {
  method: ConsoleOperation['method'];
  url: string;
  apiKey: string;
  accept?: string;
  contentType?: string;
  body?: string;
}): Record<CodeSnippetLang, string> {
  const { method, url, apiKey, accept = '*/*', contentType = 'application/json', body } = options;
  const keyPlaceholder = apiKey || 'YOUR_API_KEY';
  const upper = method.toUpperCase();
  const hasBody = Boolean(body && ['post', 'put', 'patch'].includes(method));

  const curlParts = [
    `curl -X ${upper} '${url}' \\`,
    `  -H 'Authorization: ${keyPlaceholder}' \\`,
    `  -H 'accept: ${accept}'`,
  ];
  if (hasBody) {
    curlParts[curlParts.length - 1] += ' \\';
    curlParts.push(`  -H 'Content-Type: ${contentType}' \\`);
    curlParts.push(`  -d '${body!.replace(/'/g, `'\\''`)}'`);
  }

  const python = [
    'import requests',
    '',
    `url = "${url}"`,
    'headers = {',
    `    "Authorization": "${keyPlaceholder}",`,
    `    "accept": "${accept}",`,
  ];
  if (hasBody) {
    python.push(`    "Content-Type": "${contentType}",`);
  }
  python.push('}');
  if (hasBody) {
    python.push('', `body = """${body}"""`, '');
    python.push(`response = requests.${method}(url, headers=headers, data=body, verify=True)`);
  } else {
    python.push('', `response = requests.${method}(url, headers=headers, verify=True)`);
  }
  python.push('print(response.status_code)', 'print(response.text)');

  const jsHeaderLines = [`    Authorization: "${keyPlaceholder}",`, `    accept: "${accept}",`];
  if (hasBody) {
    jsHeaderLines.push(`    "Content-Type": "${contentType}",`);
  }

  const javascript = [
    `const response = await fetch("${url}", {`,
    `  method: "${upper}",`,
    '  headers: {',
    ...jsHeaderLines,
    '  }' + (hasBody ? `,` : ''),
  ];
  if (hasBody) {
    javascript.push(`  body: ${JSON.stringify(body)},`);
  }
  javascript.push('});', '', 'const text = await response.text();', 'console.log(response.status, text);');

  const rHeaders = [
    `  "Authorization" = "${keyPlaceholder}",`,
    `  "accept" = "${accept}"` + (hasBody ? ',' : ''),
  ];
  if (hasBody) {
    rHeaders.push(`  "Content-Type" = "${contentType}"`);
  }
  const rCall = HTTR_VERBS.has(method)
    ? `${upper}(url, add_headers(.headers = headers)${hasBody ? ', body = body, encode = "raw"' : ''})`
    : `VERB("${upper}", url, add_headers(.headers = headers)${hasBody ? ', body = body, encode = "raw"' : ''})`;
  const r = [
    'library(httr)',
    '',
    `url <- "${url}"`,
    'headers <- c(',
    ...rHeaders,
    ')',
  ];
  if (hasBody) {
    r.push('', `body <- '${escapeRSingleQuoted(body!)}'`);
  }
  r.push(
    '',
    `response <- ${rCall}`,
    'print(status_code(response))',
    'print(content(response, as = "text", encoding = "UTF-8"))',
  );

  return {
    curl: curlParts.join('\n'),
    python: python.join('\n'),
    r: r.join('\n'),
    javascript: javascript.join('\n'),
  };
}

export const SAMPLE_RESPONSE = {
  status: 'OK',
  paging: {
    totalElements: 2,
    totalPages: 1,
    currentPage: 0,
  },
  payload: [
    {
      symbolOfGene: 'APOE',
      geneNcbiID: 348,
      diseaseName: "Alzheimer's Disease",
      diseaseUMLSCUI: 'C0002395',
      score: 0.9,
      ei: 0.978,
      el: 'Definitive',
      numPMIDs: 2145,
    },
    {
      symbolOfGene: 'PSEN1',
      geneNcbiID: 5663,
      diseaseName: "Alzheimer's Disease",
      diseaseUMLSCUI: 'C0002395',
      score: 0.88,
      ei: 0.95,
      el: 'Definitive',
      numPMIDs: 1280,
    },
  ],
};
